/* ComplyEdge analytics consent gate.
 *
 * WHY THIS EXISTS
 * ---------------
 * The session analytics pixel (api.ada-tool.com) writes a persistent visitor
 * identifier to localStorage on the first pageview. Under the ePrivacy
 * Directive, storing or reading an identifier on a user's device for
 * analytics is not "strictly necessary", so it requires prior consent. It
 * previously fired on load, on every public page, with no consent step.
 *
 * We sell EU compliance tooling. The bar on our own site is the bar we ask
 * customers to meet, so the pixel now loads only after an explicit choice.
 *
 * BEHAVIOUR
 *   no choice yet -> banner shown, NOTHING loaded, nothing stored
 *   accepted      -> ada_tracking.js injected, choice remembered,
 *                   same card briefly confirms, then dismisses
 *   declined      -> nothing loaded, choice remembered,
 *                   same card briefly confirms, then dismisses
 *   localhost / 127.0.0.1 -> ALWAYS show the prompt (ignore remembered
 *                   choice) so Accept/Decline confirmation can be exercised
 *                   during local preview. Production behaviour unchanged.
 *   "Analytics settings" (any [data-ce-analytics-settings] control, in the
 *                   site footer) -> reopens the same card with the current
 *                   choice, so withdrawing consent takes the same one click
 *                   as giving it (GDPR Art 7(3)). A remembered decline also
 *                   clears the pixel's two keys on every load.
 *
 * The consent record itself is the one thing stored before a choice is made,
 * which is permitted: remembering a refusal is strictly necessary to honour
 * it. It is a single key, no identifier, no network call.
 *
 * Disclosure of the recipient lives in /legal/privacy-policy.html section 9.
 *
 * Shared: the website loads this file directly; the AI Trust Center imports it
 * (apps/trust/src/analyticsConsent.ts) with the card styles in
 * analytics-consent.css, so both origins ask the same way.
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'ce-analytics-consent'; // "granted" | "denied"
  var PIXEL_SRC = 'https://api.ada-tool.com/js/ada_tracking.js';
  // Section 9 (cookies): keep the label on one line so the click target
  // is not a multi-line inline box with dead space over the parent <p>.
  // Relative on complyedge.io. A page on another origin (the AI Trust Center)
  // sets data-ce-site-origin on <html> to the website origin before this runs.
  var POLICY_PATH = '/legal/privacy-policy.html#cookies';

  function policyHref() {
    var origin = '';
    try {
      origin = document.documentElement.getAttribute('data-ce-site-origin') || '';
    } catch (e) {
      /* no <html> attribute to read: same-origin path */
    }
    return origin.replace(/\/$/, '') + POLICY_PATH;
  }
  var CONFIRM_MS = 2800;

  function isLocalDev() {
    var host = (window.location && window.location.hostname) || '';
    return host === 'localhost' || host === '127.0.0.1';
  }

  function readChoice() {
    try {
      return window.localStorage.getItem(STORAGE_KEY);
    } catch (e) {
      return null; // storage blocked: treat as undecided, never assume consent
    }
  }

  function writeChoice(value) {
    try {
      window.localStorage.setItem(STORAGE_KEY, value);
    } catch (e) {
      /* storage blocked: the choice simply is not remembered */
    }
  }

  function loadPixel() {
    if (document.querySelector('script[data-ce-analytics]')) return;
    var s = document.createElement('script');
    s.src = PIXEL_SRC;
    s.defer = true;
    s.setAttribute('data-ce-analytics', '');
    document.head.appendChild(s);
  }

  /* On a decline we also clear anything a previous grant may have left. */
  function clearPixelStorage() {
    try {
      window.localStorage.removeItem('user_id');
      window.localStorage.removeItem('session_id');
    } catch (e) {
      /* nothing to do */
    }
  }

  function clearNode(el) {
    while (el.firstChild) el.removeChild(el.firstChild);
  }

  function setConsentPad(on) {
    // Keep fixed banner from covering hero countdown / lower sections.
    try {
      document.documentElement.classList.toggle('ce-consent-open', !!on);
    } catch (e) {
      /* ignore */
    }
  }

  function dismissCard() {
    var el = document.getElementById('ce-consent');
    if (el && el.parentNode) el.parentNode.removeChild(el);
    setConsentPad(false);
  }

  function mkDismissButton() {
    var b = document.createElement('button');
    b.type = 'button';
    b.setAttribute('aria-label', 'Dismiss');
    b.setAttribute('data-ce-consent-dismiss', '');
    b.textContent = '\u00d7'; // ×
    b.className = 'ce-consent-dismiss';
    b.addEventListener('click', function () {
      if (elConfirmTimer) {
        window.clearTimeout(elConfirmTimer);
        elConfirmTimer = null;
      }
      dismissCard();
    });
    return b;
  }

  var elConfirmTimer = null;

  function showConfirmation(value) {
    var el = document.getElementById('ce-consent');
    if (!el) return;

    clearNode(el);
    el.className = 'ce-consent-card';
    el.setAttribute('role', 'status');
    el.setAttribute('aria-live', 'polite');
    el.setAttribute(
      'aria-label',
      value === 'granted' ? 'Analytics accepted' : 'Analytics declined'
    );
    el.setAttribute('data-ce-consent-state', value === 'granted' ? 'accepted' : 'declined');

    var msg = document.createElement('p');
    msg.className = 'ce-consent-msg';

    var lead = document.createElement('span');
    lead.className = 'ce-consent-lead';
    lead.textContent = value === 'granted' ? 'Analytics on.' : 'Analytics off.';
    msg.appendChild(lead);

    if (value === 'granted') {
      msg.appendChild(document.createTextNode(
        ' We will measure page reads in this browser.'
      ));
    } else {
      msg.appendChild(document.createTextNode(
        ' Nothing is measured. We will not ask again on this browser.'
      ));
    }

    el.appendChild(msg);
    el.appendChild(mkDismissButton());

    if (elConfirmTimer) {
      window.clearTimeout(elConfirmTimer);
      elConfirmTimer = null;
    }
    elConfirmTimer = window.setTimeout(function () {
      elConfirmTimer = null;
      dismissCard();
    }, CONFIRM_MS);
  }

  function currentLine(choice) {
    if (choice === 'granted') return 'Analytics is on in this browser. ';
    if (choice === 'denied') return 'Analytics is off in this browser. ';
    return '';
  }

  function decide(value) {
    writeChoice(value);
    if (value === 'granted') loadPixel();
    else clearPixelStorage();
    showConfirmation(value);
  }

  function banner(choice) {
    var wrap = document.createElement('div');
    wrap.id = 'ce-consent';
    wrap.setAttribute('role', 'dialog');
    wrap.setAttribute('aria-live', 'polite');
    wrap.setAttribute('aria-label', 'Analytics consent');
    wrap.setAttribute('data-ce-consent-state', 'prompt');
    wrap.className = 'ce-consent-card';

    var text = document.createElement('p');
    text.className = 'ce-consent-msg-banner';
    if (choice) {
      var now = document.createElement('span');
      now.className = 'ce-consent-lead';
      now.textContent = currentLine(choice);
      text.appendChild(now);
    }
    text.appendChild(document.createTextNode(
      'We would like to measure which pages people read, using an analytics ' +
      'service that stores an identifier in your browser. It is off unless you ' +
      'say yes. '
    ));
    var policy = document.createElement('a');
    policy.href = policyHref();
    policy.target = '_blank';
    policy.rel = 'noopener noreferrer';
    policy.textContent = 'How we handle data';
    policy.className = 'ce-consent-policy';
    text.appendChild(policy);
    text.appendChild(document.createTextNode('.'));

    var btns = document.createElement('div');
    btns.className = 'ce-consent-btns';

    function mkBtn(label, primary, onClick) {
      var b = document.createElement('button');
      b.type = 'button';
      b.textContent = label;
      b.className = primary
        ? 'ce-consent-btn ce-consent-btn-primary'
        : 'ce-consent-btn ce-consent-btn-ghost';
      b.addEventListener('click', onClick);
      return b;
    }

    btns.appendChild(mkBtn('Decline', false, function () { decide('denied'); }));
    btns.appendChild(mkBtn('Accept', true, function () { decide('granted'); }));

    wrap.appendChild(text);
    wrap.appendChild(btns);
    // Reopened from Analytics settings: closing keeps the current choice.
    if (choice) wrap.appendChild(mkDismissButton());
    return wrap;
  }

  function showBanner(choice) {
    if (elConfirmTimer) {
      window.clearTimeout(elConfirmTimer);
      elConfirmTimer = null;
    }
    dismissCard();
    document.body.appendChild(banner(choice));
    setConsentPad(true);
  }

  /* Footer "Analytics settings": reopen the choice. Delegated, so it works
     whatever order the footer and this script load in. */
  function onSettingsClick(e) {
    var t = e && e.target;
    var hit = t && typeof t.closest === 'function'
      ? t.closest('[data-ce-analytics-settings]')
      : null;
    if (!hit) return;
    if (typeof e.preventDefault === 'function') e.preventDefault();
    showBanner(readChoice());
  }

  function start() {
    document.addEventListener('click', onSettingsClick);
    // Local preview: always show the prompt so the in-card confirmation can be
    // checked without clearing localStorage. Production still honours the
    // remembered choice and never re-prompts after Accept/Decline.
    if (isLocalDev()) {
      document.body.appendChild(banner());
      setConsentPad(true);
      return;
    }
    var choice = readChoice();
    if (choice === 'granted') { loadPixel(); return; }
    if (choice === 'denied') { clearPixelStorage(); return; }
    document.body.appendChild(banner());
    setConsentPad(true);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
